<template>
  <!-- Main Sections -->
  <div v-for="(main_section, main_index) in preview_sections" :key="main_section.id">
    <div class="border-0 rounded-lg mt-3 mb-4 p-2">
      <!-- Main Section Header -->
      <div class="border-bottom text-center  p-3 text-x d-flex align-items-center position-relative">
        <div class="flex-grow-1">
          <p class="fw-bold  mb-0 text-capitalize">
            <i class="fa fa-folder-open me-2"></i>
            Main Section {{ main_index + 1 }} : {{ main_section.name }}
          </p>
        </div>
      </div>

      <!-- Sub-sections within this main section -->
      <div class="mt-4">
        <div v-for="(sub_section, sub_index) in main_section.sub_sections" :key="sub_section.id" class="mb-4">
          <!-- Sub-section Card -->
          <div class="glassy-card border border-secondary-subtle rounded-lg shadow-sm">
            <div class="card-header bg-light d-flex justify-content-between align-items-center rounded-top-lg">
              <p class="fw-semibold text-dark mb-0 text-capitalize">
                <i class="fas fa-edit me-1"></i>
                Sub-Section {{ sub_index + 1 }} : {{ sub_section.name }}
                <span v-if="sub_section.is_work_experience" class="badge bg-info text-white ms-2 text-xxs">Work Experience</span>
                <span v-if="has_language_cards(sub_section)" class="badge bg-primary text-white ms-2 text-xxs">Language Cards</span>
                <span v-if="has_education_cards(sub_section)" class="badge bg-success text-white ms-2 text-xxs">Education Cards</span>
                <span v-if="has_reference_cards(sub_section)" class="badge bg-secondary text-white ms-2 text-xxs">Reference Cards</span>
              </p>
              <div>
                <button
                  v-if="sub_section.questions && sub_section.questions.length > 1"
                  class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow"
                  type="button"
                  data-bs-toggle="modal"
                  data-bs-target="#reorder_questions_modal"
                  @click="open_reorder_modal(sub_section, sub_index)">
                   Reorder Questions
                </button>
              </div>
            </div>

            <!-- Questions in this sub-section (hide for WE sections — shown in card view below) -->
            <div class="card-body">
              <div v-if="get_non_we_questions(sub_section).length" v-for="(question, question_index) in get_non_we_questions(sub_section)" :key="question.id" class="mb-4">
                <!-- Question Card -->
                <div class="form-control form-control-sm rounded-lg shadow-sm bg-light">
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="d-flex flex-grow-1 gap-1">
                      <!-- Question Content -->
                      <div class="flex-grow-1">
                        <div class="mb-2">
                           <!-- Question Number -->
                           <span class="text-xs fw-bold">{{ question_index + 1 }}.</span>
                          <label class="fw-semibold text-dark text-xs text-capitalize">{{ question.text }}</label>
                          <span v-if="question.mandatory" class="text-danger ms-2 text-xxs">*</span>
                        </div>

                        <!-- Options for select-type questions -->
                        <div v-if="['multi_select', 'single_select'].includes(question.type)" class="ms-3 mt-2">
                          <p class="text-xs text-dark mb-1">Options:</p>
                          <ul class="list-unstyled ms-2">
                            <li
                              v-for="(option, index) in question.options"
                              :key="index"
                              class="text-xs text-dark mb-1"
                            >
                              <span class="text-muted me-1">•</span>
                              {{ typeof option === 'object' ? option.choice : option }}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <!-- Action Buttons for Question -->
                    <div class="d-flex flex-row gap-2 align-items-center">
                      <button
                        class="btn btn-outline-primary btn-sm border-0 text-xxs"
                        @click="edit_preview_question(question)"
                        data-bs-toggle="modal"
                        data-bs-target="#edit_preview_question"
                      >
                        <i class="bi bi-pencil-fill text-x"></i>
                      </button>

                      <button
                        class="btn btn-outline-danger btn-sm border-0 text-xxs"
                        @click="unassign_question(question.id)"
                      >
                        <i class="bi bi-trash-fill text-x"></i>
                      </button>

                      <!-- Assign Dependent Button -->
                      <button
                        v-if="['multi_select', 'single_select'].includes(question.type)"
                        @click="toggle_dependency_assignment(question.id)"
                        class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow"
                      >
                        {{ question.show_dependency_assignment ? 'Hide' : 'Assign' }} Dependent
                      </button>

                      <!-- Show/Hide Dependencies Button -->
                      <button
                        v-if="question.dependent_questions && question.dependent_questions.length > 0"
                        @click="toggle_question_dependencies(question.id)"
                        class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow"
                      >
                        {{ question.show_dependencies ? 'Hide' : 'Show' }} Dependencies
                        <span class="fw-bold ms-1">{{ question.dependent_questions.length }}</span>
                      </button>
                    </div>
                  </div>

                  <!-- Dependency Assignment UI -->
                  <div v-if="question.show_dependency_assignment && ['multi_select', 'single_select'].includes(question.type)"
                    class="mt-3 p-3 border-0 rounded-lg p-3 shadow">
                    <p class="text-xs fw-bold mb-2">Assign Dependent Question</p>
                    <div class="row g-2 align-items-end">
                      <div class="col-md-4">
                        <label class="form-label text-xxs fw-medium mb-0">Select Question</label>
                        <select v-model="question.new_dependent_id" class="form-select form-select-sm text-xxs"
                          :disabled="!available_questions_for_dependency(question.id)?.length">
                          <option value="">Select question...</option>
                          <option
                            v-for="avail_question in available_questions_for_dependency(question.id)"
                            :key="avail_question.id"
                            :value="avail_question.id"
                          >
                            {{ avail_question.question_text?.substring(0, 35) }}
                            {{ avail_question.question_text?.length > 35 ? '...' : '' }}
                          </option>
                        </select>
                      </div>

                      <div v-if="question.new_dependent_id" class="col-md-3">
                        <label class="form-label text-xxs fw-medium mb-0">When answered</label>
                        <select v-model="question.trigger_condition" class="form-select form-select-sm text-xxs">
                          <option value="">Select value...</option>
                          <option
                            v-for="option in question.options"
                            :key="option.id || option"
                            :value="typeof option === 'object' ? option.choice : option"
                          >
                            {{ typeof option === 'object' ? option.choice : option }}
                          </option>
                        </select>
                      </div>

                      <div v-if="question.new_dependent_id" class="col-md-2">
                        <label class="form-label text-xxs fw-medium mb-0">Required</label>
                        <select v-model="question.is_dependent_mandatory" class="form-select form-select-sm text-xxs">
                          <option v-for="option in backend_options" :value="option.value">
                            {{ option.text }}
                          </option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <button
                          @click="add_dependent_question(question)"
                          :disabled="!question.new_dependent_id || !question.trigger_condition"
                          class="btn btn-primary-dark btn-sm text-xxs shadow"
                        >
                          Assign Dependent
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Dependent Questions -->
                  <div v-if="question.show_dependencies && question.dependent_questions && question.dependent_questions.length > 0"
                    class="mt-3 ms-4 border-start border-3 border-primary ps-3">
                    <p class="text-xs fw-bold mb-2 text-dark">
                      <i class="bi bi-link-45deg"></i>
                      Dependent Questions
                    </p>

                    <!-- Dependent Question Items -->
                    <PreviewFormRecursiveDependentQuestion
                      v-for="(dependent_question, dep_index) in question.dependent_questions"
                      :key="dependent_question.question_id || dependent_question.id"
                      :node="dependent_question"
                      :index="dep_index"
                      @edit="edit_dependency_question"
                      @remove="(node) => remove_dependent_question(question.id, node.question_id || node.id)"
                    />
                  </div>
                </div>
              </div>

              <!-- Empty State for Questions -->
              <div v-if="get_non_we_questions(sub_section).length === 0 && !sub_section.is_work_experience && !has_language_cards(sub_section) && !has_education_cards(sub_section) && !has_reference_cards(sub_section)" class="text-center py-4">
                <i class="bi bi-question-circle text-x text-muted mb-2"></i>
                <p class="text-muted">No questions in this sub-section</p>
              </div>

              <!-- Work Experience Preview: 3 cards showing how entity users will see it -->
              <div v-if="sub_section.is_work_experience" class="p-3">
                <div class="d-flex align-items-center mb-3">
                  <i class="bi bi-briefcase me-2 text-primary"></i>
                  <p class="text-xs fw-bold mb-0">Work Experience Preview</p>
                  <span class="badge bg-info text-white ms-2 text-xxs">3 blocks</span>
                </div>

                <div
                  v-for="card_idx in 3"
                  :key="'we-preview-card-' + card_idx"
                  class="border rounded-lg p-3 mb-3 bg-white shadow-sm"
                >
                  <p class="fw-bold text-xs mb-2 text-dark">
                    <i class="bi bi-building me-1"></i>
                    Experience {{ card_idx }}
                  </p>

                  <div class="row g-2">
                    <div class="col-md-6">
                      <label class="text-xxs text-muted d-block mb-0">Employer <span class="text-danger">*</span></label>
                      <div class="form-control form-control-sm text-xxs bg-light border-1 rounded-lg" style="min-height:30px;"><span class="text-muted fst-italic">Sample input</span></div>
                    </div>
                    <div class="col-md-6">
                      <label class="text-xxs text-muted d-block mb-0">Post Held <span class="text-danger">*</span></label>
                      <div class="form-control form-control-sm text-xxs bg-light border-1 rounded-lg" style="min-height:30px;"><span class="text-muted fst-italic">Sample input</span></div>
                    </div>
                    <div class="col-md-3">
                      <label class="text-xxs text-muted d-block mb-0">From Month <span class="text-danger">*</span></label>
                      <div class="form-control form-control-sm text-xxs bg-light border-1 rounded-lg" style="min-height:30px;"><span class="text-muted fst-italic">MM</span></div>
                    </div>
                    <div class="col-md-3">
                      <label class="text-xxs text-muted d-block mb-0">From Year <span class="text-danger">*</span></label>
                      <div class="form-control form-control-sm text-xxs bg-light border-1 rounded-lg" style="min-height:30px;"><span class="text-muted fst-italic">YYYY</span></div>
                    </div>
                    <div class="col-md-3">
                      <label class="text-xxs text-muted d-block mb-0">To Month</label>
                      <div class="form-control form-control-sm text-xxs bg-light border-1 rounded-lg" style="min-height:30px;"><span class="text-muted fst-italic">MM</span></div>
                    </div>
                    <div class="col-md-3">
                      <label class="text-xxs text-muted d-block mb-0">To Year</label>
                      <div class="form-control form-control-sm text-xxs bg-light border-1 rounded-lg" style="min-height:30px;"><span class="text-muted fst-italic">YYYY</span></div>
                    </div>
                    <div class="col-12">
                      <label class="text-xxs text-muted d-block mb-0">Reason for Leaving</label>
                      <div class="form-control form-control-sm text-xxs bg-light border-1 rounded-lg" style="min-height:30px;"><span class="text-muted fst-italic">Sample input</span></div>
                    </div>
                  </div>

                  <div class="form-check mt-2">
                    <input class="form-check-input" type="checkbox" disabled />
                    <label class="form-check-label text-xxs text-muted">I currently work here</label>
                  </div>
                </div>

                <p class="text-xxs text-muted mt-1"><i class="bi bi-info-circle me-1"></i>Entity users start with 1 block and can add up to 3. Only one can be marked as "currently work here".</p>
              </div>

              <!-- Language Cards Preview: grouped by block like Work Experience -->
              <div v-if="has_language_cards(sub_section)" class="p-3">
                <div class="d-flex align-items-center mb-3">
                  <i class="bi bi-translate me-2 text-primary"></i>
                  <p class="text-xs fw-bold mb-0">Language Proficiency Preview</p>
                  <span class="badge bg-primary text-white ms-2 text-xxs">{{ get_language_cards(sub_section).length }} blocks</span>
                </div>

                <div
                  v-for="card in get_language_cards(sub_section)"
                  :key="'lang-preview-card-' + card.block_num"
                  class="border rounded-lg p-3 mb-3 bg-white shadow-sm"
                >
                  <p class="fw-bold text-xs mb-2 text-dark">
                    <i class="bi bi-translate me-1"></i>
                    Language {{ card.block_num }}
                  </p>

                  <div class="row g-2">
                    <div
                      v-for="(q, qi) in card.questions"
                      :key="'lang-q-' + q.id"
                      :class="card.questions.length <= 3 ? 'col-md-4' : 'col-md-6'"
                    >
                      <label class="text-xxs text-muted d-block mb-0">
                        {{ _strip_card_prefix(q.text) }}
                        <span v-if="q.mandatory" class="text-danger">*</span>
                      </label>
                      <div v-if="q.type === 'single_select' || q.type === 'multi_select'"
                        class="form-control form-control-sm text-xxs bg-light border-1 rounded-lg" style="min-height:30px;">
                        <span class="text-muted fst-italic">Dropdown ·
                          {{ (q.options || []).slice(0, 3).map(o => typeof o === 'object' ? o.choice : o).join(', ') }}{{ (q.options || []).length > 3 ? '...' : '' }}
                        </span>
                      </div>
                      <div v-else class="form-control form-control-sm text-xxs bg-light border-1 rounded-lg" style="min-height:30px;">
                        <span class="text-muted fst-italic">Sample input</span>
                      </div>
                    </div>
                  </div>
                </div>

                <p class="text-xxs text-muted mt-1"><i class="bi bi-info-circle me-1"></i>Each card includes language selection, speaking level, and reading/writing level with dropdown options.</p>
              </div>

              <!-- Education Cards Preview: grouped by block like Work Experience -->
              <div v-if="has_education_cards(sub_section)" class="p-3">
                <div class="d-flex align-items-center mb-3">
                  <i class="bi bi-mortarboard me-2 text-success"></i>
                  <p class="text-xs fw-bold mb-0">Formal Qualification Preview</p>
                  <span class="badge bg-success text-white ms-2 text-xxs">{{ get_education_cards(sub_section).length }} blocks</span>
                </div>

                <div
                  v-for="card in get_education_cards(sub_section)"
                  :key="'edu-preview-card-' + card.block_num"
                  class="border rounded-lg p-3 mb-3 bg-white shadow-sm"
                >
                  <p class="fw-bold text-xs mb-2 text-dark">
                    <i class="bi bi-mortarboard me-1"></i>
                    Qualification {{ card.block_num }}
                  </p>

                  <div class="row g-2">
                    <div
                      v-for="(q, qi) in card.questions"
                      :key="'edu-q-' + q.id"
                      :class="card.questions.length <= 3 ? 'col-md-4' : 'col-md-6'"
                    >
                      <label class="text-xxs text-muted d-block mb-0">
                        {{ _strip_card_prefix(q.text) }}
                        <span v-if="q.mandatory" class="text-danger">*</span>
                      </label>
                      <div v-if="q.type === 'single_select' || q.type === 'multi_select'"
                        class="form-control form-control-sm text-xxs bg-light border-1 rounded-lg" style="min-height:30px;">
                        <span class="text-muted fst-italic">Dropdown ·
                          {{ (q.options || []).slice(0, 3).map(o => typeof o === 'object' ? o.choice : o).join(', ') }}{{ (q.options || []).length > 3 ? '...' : '' }}
                        </span>
                      </div>
                      <div v-else class="form-control form-control-sm text-xxs bg-light border-1 rounded-lg" style="min-height:30px;">
                        <span class="text-muted fst-italic">Sample input</span>
                      </div>
                    </div>
                  </div>
                </div>

                <p class="text-xxs text-muted mt-1"><i class="bi bi-info-circle me-1"></i>Each card captures institution, qualification, year details, and NQF level with dropdown support.</p>
              </div>

              <!-- Reference Cards Preview: grouped by block like Work Experience -->
              <div v-if="has_reference_cards(sub_section)" class="p-3">
                <div class="d-flex align-items-center mb-3">
                  <i class="bi bi-people me-2 text-secondary"></i>
                  <p class="text-xs fw-bold mb-0">References Preview</p>
                  <span class="badge bg-secondary text-white ms-2 text-xxs">{{ get_reference_cards(sub_section).length }} blocks</span>
                </div>

                <div
                  v-for="card in get_reference_cards(sub_section)"
                  :key="'ref-preview-card-' + card.block_num"
                  class="border rounded-lg p-3 mb-3 bg-white shadow-sm"
                >
                  <p class="fw-bold text-xs mb-2 text-dark">
                    <i class="bi bi-person me-1"></i>
                    Reference {{ card.block_num }}
                  </p>

                  <div class="row g-2">
                    <div
                      v-for="(q, qi) in card.questions"
                      :key="'ref-q-' + q.id"
                      :class="card.questions.length <= 3 ? 'col-md-4' : 'col-md-6'"
                    >
                      <label class="text-xxs text-muted d-block mb-0">
                        {{ _strip_card_prefix(q.text) }}
                        <span v-if="q.mandatory" class="text-danger">*</span>
                      </label>
                      <div v-if="q.type === 'single_select' || q.type === 'multi_select'"
                        class="form-control form-control-sm text-xxs bg-light border-1 rounded-lg" style="min-height:30px;">
                        <span class="text-muted fst-italic">Dropdown ·
                          {{ (q.options || []).slice(0, 3).map(o => typeof o === 'object' ? o.choice : o).join(', ') }}{{ (q.options || []).length > 3 ? '...' : '' }}
                        </span>
                      </div>
                      <div v-else class="form-control form-control-sm text-xxs bg-light border-1 rounded-lg" style="min-height:30px;">
                        <span class="text-muted fst-italic">Sample input</span>
                      </div>
                    </div>
                  </div>
                </div>

                <p class="text-xxs text-muted mt-1"><i class="bi bi-people me-1"></i>Each card includes name, relationship (dropdown with Other option), and office-hours telephone.</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State for Sub-sections -->
        <div v-if="!main_section.sub_sections || main_section.sub_sections.length === 0" class="text-center py-4">
          <i class="bi bi-folder-x text-x text-muted mb-2"></i>
          <p class="text-muted">No sub-sections in this main section</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Reorder Questions Modal (Keep as is) -->
<teleport to="body">
  <div class="modal fade" id="reorder_questions_modal" tabindex="-1" aria-labelledby="reorder_questions_label"
    aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content bg-white rounded-3 shadow-lg">
        <div class="modal-header border-bottom-0 cute-header shadow-lg">
          <p class="modal-title text-white text-xxs">Reorder Questions</p>
          <button type="button" class="modal-btn-close btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body overflow-auto">
          <div class="reorder-instructions text-muted text-xxs mb-3 p-2 bg-info-subtle border-start border-3 rounded">
            <i class="fas fa-info-circle"></i> Drag questions up or down to reorder them
          </div>

          <div class="sortable-list" id="sortable-questions-list">
            <div v-for="(question, index) in current_reorder_questions" :key="question.id"
              class="sortable-item mb-2 p-2 border rounded bg-light position-relative cursor-move transition"
              draggable="true" @dragstart="drag_start($event, index)" @dragover="drag_over($event)"
              @drop="drag_drop($event, index)" @dragend="drag_end($event)">
              <div class="d-flex align-items-center">
                <div class="drag-handle me-3 text-muted"><i class="fas fa-grip-vertical"></i></div>
                <div
                  class="question-number card-header-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 text-center text-nowrap text-xs">
                  {{ index + 1 }}
                </div>
                <div class="flex-grow-1">
                  <div class="question-title fw-bold text-xs text-dark">{{ question.text || question.question_text }}</div>
                  <div class="question-type text-muted text-xxs">{{
                   format_question_type(question.type) }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer border-0 p-2 d-flex justify-content-end">
          <button type="button" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
            @click="save_question_order" data-bs-dismiss="modal">
            Update Order
          </button>
        </div>
      </div>
    </div>
  </div>
  </div>
</teleport>

  <!-- Edit Question Modal (Keep as is) -->
<teleport to="body">
  <div class="modal fade" id="edit_preview_question" tabindex="-1" aria-labelledby="edit_preview_question_label"
    aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content bg-white rounded-3 shadow-lg border-0">
        <div class="modal-header border-bottom-0 cute-header shadow-lg">
          <p class="modal-title text-xxs">
            {{ current_edit_mode === 'dependent_question' ? 'Edit Dependent Question' : 'Edit Question' }}
          </p>
          <button type="button" class="modal-btn-close btn-close" data-bs-dismiss="modal" aria-label="Close"
            @click="reset_edit_state"></button>
        </div>

        <div class="modal-body">
          <!-- Dependent Question Form -->
          <div v-if="current_edit_mode === 'dependent_question'" class="modal-form-group form-group mb-3">
              <label for="question" class="modal-form-label form-label text-xxs">Question:</label>
              <input type="text" v-model="new_dependent_question.question_text"
                  class="modal-form-control form-control text-xxs rounded-3 p-2 shadow-sm mb-3" placeholder="Question here">

              <label for="question_type_name" class="modal-form-label form-label text-xxs">Question Type:</label>
              <select v-model="new_dependent_question.question_type_id"
                  class="modal-form-select form-select text-xxs rounded-3 p-2 shadow-sm mb-3"
                  @change="handle_question_type_change(new_dependent_question)">
                  <option value="" disabled>Select Type</option>
                  <option v-for="type in question_types" :key="type.id" :value="type.id">{{ type.formatted_type ||
                      type.question_type }}</option>
              </select>


              <label class="modal-form-label form-label text-xxs">Trigger Condition:</label>
              <input type="text" v-model="new_dependent_question.trigger_condition"
                  class="modal-form-control form-control text-xxs rounded-3 p-2 shadow-sm mb-3"
                  placeholder="Trigger condition">

              <!-- Options for select-type questions -->
              <div v-if="new_dependent_question.show_option_inputs" class="mt-3">
                  <div class="row mb-2">
                      <div class="col-12 d-flex justify-content-between align-items-center">
                          <label class="modal-form-label form-label text-xxs mb-0">Options</label>

                          <button
                            type="button"
                            @click="new_dependent_question.options.push({ choice: '' })"
                            class="btn btn-primary btn-sm rounded-3 fw-bold text-xxs shadow me-2"
                          >
                            <i class="fas fa-plus"></i>
                          </button>
                      </div>
                  </div>

                  <div v-for="(option, index) in new_dependent_question.options" :key="index" class="mb-2 position-relative">
                      <input
                          v-model="option.choice"
                          type="text"
                          class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
                          :placeholder="'Option ' + (index + 1)"
                      />
                      <!-- remove button -->
                      <span
                          @click="new_dependent_question.options.splice(index, 1)"
                          class="remove-option-icon position-absolute end-0 top-50 translate-middle-y me-2 cursor-pointer"
                          v-if="new_dependent_question.options.length > 2"
                      >
                          <i class="fas fa-times small-icon"></i>
                      </span>
                  </div>
              </div>


              <div class="form-check mb-3">
                  <input type="checkbox" v-model="new_dependent_question.is_mandatory" class="form-check-input"
                      id="dependent_mandatory">
                  <label class="form-check-label text-xxs" for="dependent_mandatory">Mandatory Question</label>
              </div>
          </div>

          <!-- Main Question Form -->
          <div v-else class="modal-form-group form-group mb-3">
            <label for="question" class="modal-form-label form-label text-xxs">Question:</label>
            <input type="text" v-model="current_edit_question.question_text"
              class="modal-form-control form-control text-xxs rounded-3 p-2 shadow-sm mb-3" placeholder="Question here">

            <label for="question_type_name" class="modal-form-label form-label text-xxs">Question Type:</label>
            <select v-model="current_edit_question.question_type_id"
              class="modal-form-select form-select text-xxs rounded-3 p-2 shadow-sm mb-3"
              @change="handle_question_type_change(current_edit_question)">
              <option value="" disabled>Select Type</option>
              <option v-for="type in question_types" :key="type.id" :value="type.id">{{ type.formatted_type }}</option>
            </select>


            <div v-if="current_edit_question.show_option_inputs" class="mt-3">
              <div class="row mb-2">
                <div class="col-12 d-flex justify-content-between align-items-center">
                  <label class="modal-form-label form-label text-xxs mb-0">Options</label>
                  <button @click="add_option_input(current_edit_question)"
                    class="btn btn-primary btn-sm rounded-3 fw-bold text-xxs shadow me-2">
                    <i class="fas fa-plus"></i>
                  </button>
                </div>
              </div>

              <div v-for="(option, index) in current_edit_question.options" :key="option.id || index"
                class="mb-2 position-relative">
                <input v-model="option.choice" type="text"
                  class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
                  :placeholder="'Option ' + (index + 1)">
                <span @click="remove_option_input(current_edit_question, index)"
                  class="remove-option-icon position-absolute end-0 top-50 translate-middle-y me-2 cursor-pointer"
                  v-if="current_edit_question.options && current_edit_question.options.length > 2">
                  <i class="fas fa-times small-icon"></i>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Save Button Aligned -->
        <div class="modal-footer border-0 p-2 d-flex justify-content-end">
          <button type="button" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
            @click="handle_save_edit">
            Save
          </button>
        </div>
      </div>
    </div>
  </div>
</teleport>
  
</template>